import { z } from "zod"

// In order to work towards making visualizations a plugin, we postpone type checking
// until the visualization is actually used. We use zod to define every type, so that
// we can parse the visualizations argument in PropsUIPromptConsentFormTable.

// Matching types from Feldspar
// We can either import these from Feldspare, or keep visualization plugin separate b
// duplicating the types here. Currently opting for duplication to avoid complexity
// (and if input format changes, the plugin would break regardless)

// Permissive by design: any locale key is accepted here. Strictness belongs at
// the researcher-facing gates (where a bad config should be reported), not at
// the participant's render path — here the runtime defense is the resolver
// sentinel from src/locale/text.ts, which returns '?text?' instead of throwing.
export const zTranslatable = z.record(z.string(), z.string())
export type Translatable = z.infer<typeof zTranslatable>

export const zLabel = z.union([zTranslatable, z.string()])
export type Label = z.infer<typeof zLabel>

// Table type, but only taking what we need.
// body.rows is validated in place via z.custom and passed through BY REFERENCE:
// zod's normal array/object schemas reconstruct (deep-clone) their output, which
// for a 65k-row table retained one full copy per figure for the page lifetime
// (issue #122). The custom predicate keeps zod as the validation gate with zero
// row allocations. Trade-off: a validation failure yields one custom issue, not
// zod's per-path nested errors.
// zTableRow is the shape's source of truth; isRowArray is its zero-allocation
// implementation (conformance-tested).
export const zTableRow = z.object({ id: z.string(), cells: z.array(z.string()) })
export type TableRow = z.infer<typeof zTableRow>

export function isRowArray (rows: unknown): rows is TableRow[] {
  return (
    Array.isArray(rows) &&
    rows.every(
      (row) =>
        row != null &&
        typeof (row as TableRow).id === 'string' &&
        Array.isArray((row as TableRow).cells) &&
        (row as TableRow).cells.every((cell) => typeof cell === 'string')
    )
  )
}

export const zTable = z.object({
  id: z.string(),
  head: z.object({ cells: z.array(z.string()) }),
  body: z.object({ rows: z.custom<TableRow[]>(isRowArray, { message: 'body.rows must be an array of { id: string, cells: string[] }' }) }),
})
export type Table = z.infer<typeof zTable>

// Visualization Types

export const zVisualizationProps = z.object({
  title: zTranslatable,
  height: z.number().optional(),
})
export type VisualizationProps = z.infer<typeof zVisualizationProps>

export const zAggregationFunction = z.enum(["count", "mean", "sum", "count_pct", "pct"])
export type AggregationFunction = z.infer<typeof zAggregationFunction>

export const zDateFormat = z.enum([
  "auto",
  "year",
  "quarter",
  "month",
  "day",
  "hour",
  "month_cycle",
  "weekday_cycle",
  "hour_cycle",
])
export type DateFormat = z.infer<typeof zDateFormat>

export const zChartVisualizationType = z.enum(["line", "bar", "area"])
export type ChartVisualizationType = z.infer<typeof zChartVisualizationType>

export const zTextVisualizationType = z.enum(["wordcloud"])
export type TextVisualizationType = z.infer<typeof zTextVisualizationType>

// Chart Visualizations

// External types (need schema)
export const zAxis = z.object({
  label: zLabel.optional(),
  column: z.string(),
})
export type Axis = z.infer<typeof zAxis>

export const zAggregationGroup = z.object({
  label: zLabel.optional(),
  column: z.string(),
  dateFormat: zDateFormat.optional(),
  range: z.array(z.number()).optional(),
  levels: z.array(z.string()).optional(),
  // Keep only the N groups with the largest first-series value (categorical
  // groups only -- ignored when dateFormat is set). See prepareChartData.ts.
  top: z.number().int().positive().optional(),
})
export type AggregationGroup = z.infer<typeof zAggregationGroup>

export const zAggregationValue = z.object({
  label: zLabel.optional(),
  column: z.string().optional().default(".COUNT"),
  aggregate: zAggregationFunction.optional(),
  group_by: z.string().optional(),
  z: z.string().optional(),
  zAggregate: zAggregationFunction.optional(),
  addZeroes: z.boolean().optional(),
})
export type AggregationValue = z.infer<typeof zAggregationValue>

export const zChartVisualization = zVisualizationProps.extend({
  type: zChartVisualizationType,
  group: zAggregationGroup,
  values: z.array(zAggregationValue),
})
export type ChartVisualization = z.infer<typeof zChartVisualization>

// Internal types
export type TickerFormat = "percent" | "default"
export type XType = "string" | "date"

export interface AxisSettings {
  id: string
  label: Translatable | string
  tickerFormat: TickerFormat
}

export interface ChartVisualizationData {
  type: ChartVisualizationType
  data: Array<Record<string, any>>
  xKey: string
  xLabel: string | Translatable | undefined
  yKeys: Record<string, AxisSettings>
}

// Text Visualizations

// External types (need schema)

export const zTextVisualization = zVisualizationProps.extend({
  type: zTextVisualizationType,
  textColumn: z.string(),
  valueColumn: z.string().optional(),
  tokenize: z.boolean().optional(),
  extract: z.enum(["url_domain"]).optional(),
  // Drops any token beginning with '@' (a WhatsApp-style @mention) --
  // catches only the mention's first word; the rest of a mentioned name
  // (WhatsApp writes it out in full: "@John Doe") is caught separately by
  // excludeColumn. See prepareTextData.ts.
  stripMentions: z.boolean().optional(),
  // Every distinct value of this column, lowercased and split on whitespace,
  // joins the stopword set -- for a chat table this is the participant-name
  // column, so a group member's own name never dominates their own
  // wordcloud. See prepareTextData.ts.
  excludeColumn: z.string().optional(),
  // Drops media-placeholder text ("<Media omitted>", "<Media weggelaten>",
  // and the bare forms without brackets) before tokenizing. See
  // PLACEHOLDER_PHRASES in prepareTextData.ts.
  stripPlaceholders: z.boolean().optional(),
})
export type TextVisualization = z.infer<typeof zTextVisualization>

// Internal types

export interface ScoredTerm {
  text: string
  value: number
  importance: number
  rowIds?: string[]
}

export interface TextVisualizationData {
  type: TextVisualizationType
  topTerms: ScoredTerm[]
}

// Stats Visualizations

// External types (need schema)

export const zStatAggregateFunction = z.enum([
  "count",
  "distinct",
  "min",
  "max",
  "sum",
  "mean",
  "first_date",
  "last_date",
  "span_days",
  "busiest_day",
])
export type StatAggregateFunction = z.infer<typeof zStatAggregateFunction>

export const zStatTile = z.object({
  label: zTranslatable,
  aggregate: zStatAggregateFunction,
  column: z.string().optional(),
  dateFormat: zDateFormat.optional(),
}).superRefine((tile, ctx) => {
  // 'count' is the only aggregate that can run over the whole table (row
  // count); every other aggregate needs a column to read (see README.md).
  if (tile.aggregate !== 'count' && tile.column === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['column'],
      message: `'column' is required for aggregate '${tile.aggregate}'`,
    })
  }
})
export type StatTile = z.infer<typeof zStatTile>

export const zStatsVisualizationType = z.enum(["stats"])
export type StatsVisualizationType = z.infer<typeof zStatsVisualizationType>

export const zStatsVisualization = zVisualizationProps.extend({
  type: zStatsVisualizationType,
  tiles: z.array(zStatTile).min(1),
})
export type StatsVisualization = z.infer<typeof zStatsVisualization>

// Internal types

export interface StatTileData {
  label: Translatable
  value: string
}

export interface StatsVisualizationData {
  type: StatsVisualizationType
  tiles: StatTileData[]
}

// Heatmap Visualizations

// External types (need schema)

export const zHeatmapMode = z.enum(["calendar", "weekday_hour"])
export type HeatmapMode = z.infer<typeof zHeatmapMode>

export const zHeatmapAggregate = z.enum(["count", "sum", "mean"])
export type HeatmapAggregate = z.infer<typeof zHeatmapAggregate>

export const zHeatmapVisualizationType = z.enum(["heatmap"])
export type HeatmapVisualizationType = z.infer<typeof zHeatmapVisualizationType>

export const zHeatmapVisualization = zVisualizationProps.extend({
  type: zHeatmapVisualizationType,
  mode: zHeatmapMode,
  dateColumn: z.string(),
  valueColumn: z.string().optional(),
  aggregate: zHeatmapAggregate.optional(),
})
export type HeatmapVisualization = z.infer<typeof zHeatmapVisualization>

// Internal types

export interface HeatmapGrid {
  // "" for weekday_hour (a single grid); the year (e.g. "2024") per calendar grid
  key: string
  // Two-letter weekday abbreviations (Mo Tu We Th Fr Sa Su), Monday-first.
  rowLabels: string[]
  // Full weekday names (locale-resolved), parallel to rowLabels, for tooltips.
  rowTooltipLabels: string[]
  colLabels: string[]
  values: number[][]
  // Present for calendar grids only: the ISO date (yyyy-mm-dd) each cell
  // represents, or '' for a padding cell outside the year -- used for
  // tooltips and to skip drawing padding cells.
  cellDates?: string[][]
  // Present for calendar grids only: month-boundary labels along the columns.
  colGroups?: Array<{ col: number, label: string }>
}

export interface HeatmapVisualizationData {
  type: HeatmapVisualizationType
  mode: HeatmapMode
  max: number
  grids: HeatmapGrid[]
}

// Thread Visualizations
//
// One viewer serves every "grouped conversation" export -- ChatGPT
// conversations today, WhatsApp / Meta message exports later (see
// README.md). A table with no natural group column (a flat chat export)
// renders as a single thread via `singleThreadTitle` instead of `groupColumn`
// -- exactly one of the two is required (enforced below, not by the type
// system: zod unions of "optional-but-one-required" fields don't narrow
// cleanly, and a superRefine gives a clearer message than a discriminated
// union would here).

// External types (need schema)

export const zThreadVisualizationType = z.enum(["thread"])
export type ThreadVisualizationType = z.infer<typeof zThreadVisualizationType>

export const zThreadPageSize = z.object({
  threads: z.number().int().positive().optional(),
  turns: z.number().int().positive().optional(),
})
export type ThreadPageSize = z.infer<typeof zThreadPageSize>

export const zThreadVisualization = zVisualizationProps.extend({
  type: zThreadVisualizationType,
  // The grouping key: two rows with the same groupColumn value are the same
  // thread. Use a stable id column here, not a display title -- two distinct
  // threads can carry the same title (a renamed or never-renamed chat), and
  // grouping by title alone would silently merge them.
  groupColumn: z.string().optional(),
  // The column supplying each thread's displayed title. Defaults to
  // groupColumn itself, so a table whose grouping key is already
  // display-worthy (e.g. a WhatsApp chat's single synthetic group) needs
  // nothing extra; a ChatGPT-style table groups by id and points this at
  // the title column instead.
  titleColumn: z.string().optional(),
  // Label, not a plain string: it is UI copy the researcher writes (like
  // `title`), not a value read off a table row -- resolved with
  // resolveFlatText at render time, same as a grouped thread's title (a raw
  // table cell) is.
  singleThreadTitle: zLabel.optional(),
  roleColumn: z.string(),
  textColumn: z.string(),
  timeColumn: z.string().optional(),
  badgeColumn: z.string().optional(),
  selfRole: z.string().optional(),
  pageSize: zThreadPageSize.optional(),
}).superRefine((viz, ctx) => {
  if (viz.groupColumn === undefined && viz.singleThreadTitle === undefined) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['groupColumn'],
      message: "either 'groupColumn' or 'singleThreadTitle' is required",
    })
  }
})
export type ThreadVisualization = z.infer<typeof zThreadVisualization>

// Internal types

export interface ThreadTurn {
  role: string
  text: string
  // ISO 8601, present only when timeColumn is configured and the cell parses.
  time?: string
  badge?: string
}

export interface Thread {
  id: string
  // The group column's raw cell value (grouped mode) or singleThreadTitle
  // (single-thread mode) -- a Label either way, resolved with
  // resolveFlatText at render time (a raw string resolves to itself).
  title: Label
  count: number
  // ISO 8601, present only when at least one turn's timeColumn cell parses.
  firstTime?: string
  lastTime?: string
  turns: ThreadTurn[]
}

export const DEFAULT_THREAD_PAGE_SIZE = { threads: 20, turns: 50 }

export interface ThreadVisualizationData {
  type: ThreadVisualizationType
  threads: Thread[]
  // True when more than 5000 groups were found and the tail was dropped.
  truncated: boolean
  // The number of groups found before capping -- equal to threads.length
  // when truncated is false. Lets the figure show "first N of TOTAL" without
  // knowing the cap itself.
  totalThreads: number
  // Defaults already resolved here, so figures/thread_view.tsx never needs
  // the original visualization config.
  pageSize: { threads: number, turns: number }
  // Carried through so the figure knows which turns to right-align without
  // also needing the original visualization config. Undefined (WhatsApp
  // reuse, see README.md) renders every turn left-aligned.
  selfRole?: string
}

// Visualization Type union

export type VisualizationData = ChartVisualizationData | TextVisualizationData | StatsVisualizationData | HeatmapVisualizationData | ThreadVisualizationData

export const zVisualizationType = z.union([zChartVisualization, zTextVisualization, zStatsVisualization, zHeatmapVisualization, zThreadVisualization])
export type VisualizationType = z.infer<typeof zVisualizationType>
